<template>
  <!-- A click inside never reaches the row, thread or note around it. -->
  <div
    class="attachment-video"
    data-test="attachment-video"
    @click.stop
  >
    <!-- Playing: the only state that has a <video>, and the box keeps the
         card's height so the thread does not jump when it appears. -->
    <div
      v-if="phase === 'playing'"
      class="video-box"
      data-test="video-box"
    >
      <!-- Same-origin, so no crossorigin: the session cookie rides along on
           every Range request. controlslist hides Chrome's own Download item,
           which would fetch this capped playback url rather than download=1. -->
      <video
        ref="video"
        :src="videoUrl"
        :aria-label="displayName"
        controls
        playsinline
        preload="metadata"
        disableremoteplayback
        controlslist="nodownload"
        @loadedmetadata="onMetadata"
        @error="onMediaError"
      />
    </div>

    <div
      v-else-if="phase === 'checking'"
      class="video-card"
      data-test="video-checking"
    >
      <font-awesome-icon :icon="['fas', 'file-video']" />
      <span>Checking the video...</span>
    </div>

    <div
      v-else-if="phase === 'unplayable'"
      class="video-card"
      data-test="video-unplayable"
    >
      <font-awesome-icon :icon="['fas', 'file-video']" />
      <span class="video-card-text">{{ unplayableText }}</span>
      <span
        v-if="downloadMessage"
        class="video-card-text"
        data-test="video-download-message"
      >{{ downloadMessage }}</span>
      <button
        v-if="canDownload"
        type="button"
        class="video-action"
        data-test="video-download"
        :disabled="downloading"
        @click="download"
      >
        Download
      </button>
      <!-- Once: a codec this browser lacks fails the same way every time, but
           a stream that broke for another reason may play on a second try. -->
      <button
        v-if="!retriedUnplayable"
        type="button"
        class="video-action"
        data-test="video-unplayable-retry"
        @click="retryUnplayable"
      >
        Try again
      </button>
    </div>

    <div
      v-else-if="phase === 'scan_pending' || phase === 'unavailable'"
      class="video-card video-state"
      data-test="video-state"
    >
      <font-awesome-icon :icon="['fas', 'file-video']" />
      <span>{{ verdictText }}</span>
      <span class="video-card-name">{{ displayName }}</span>
    </div>

    <div
      v-else-if="phase === 'retry'"
      class="video-card"
      data-test="video-error"
    >
      <font-awesome-icon :icon="['fas', 'file-video']" />
      <span class="video-card-text">{{ retryText }}</span>
      <button
        type="button"
        class="video-action"
        data-test="video-retry"
        @click="retry"
      >
        Try again
      </button>
    </div>

    <!-- Idle: a card, never a <video>. Nothing is requested until the click. -->
    <div
      v-else
      class="video-box video-idle"
      data-test="video-idle"
    >
      <button
        v-if="canPlay"
        type="button"
        class="video-play"
        data-test="video-play"
        :aria-label="`Play video ${displayName}`"
        @click="startPlayback"
      >
        <font-awesome-icon :icon="['fas', 'play']" />
      </button>
      <font-awesome-icon
        v-else
        :icon="['fas', 'file-video']"
        class="video-idle-icon"
      />
      <div class="video-caption">
        <span
          class="video-name"
          data-test="video-name"
        >{{ displayName }}</span>
        <span
          v-if="sizeLabel"
          class="video-size"
          data-test="video-size"
        >{{ sizeLabel }}</span>
        <span
          v-if="advisory"
          class="video-advisory"
          data-test="video-advisory"
        >{{ advisory }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DOCUMENT_UNAVAILABLE_TEXT,
  SCAN_PENDING_TEXT,
  isFileUid,
  keyonFileUrl,
} from "@/utils/fileUrl";
import {
  attachmentPresentation,
  formatAttachmentSize,
  getAttachmentName,
  withPlaybackFailure,
} from "@/utils/messageAttachments";
import { downloadRefusalText } from "@/api/keyonFiles";
import { probeKeyonVideo, startKeyonFileDownload } from "@/api/keyonVideo";

// The one player that has a <video> mounted, as { owner, release }. A second
// Play releases it: every open element holds a Range stream against keyon (a
// prefork worker per slice), and keyon refuses a fifth stream per person
// (stream_busy), so a thread full of videos must not be able to hold them all open.
let active = null;

const RETRY_TEXT = {
  rate_limited: "Too many files are open right now. Wait a moment, then try again.",
  network: "The video stopped loading. Please try again.",
  error: "Unable to play this video. Please try again.",
};

// MediaError.code for a fetch that failed part-way (a slice refused 429
// stream_busy, a 5xx, a reset). The others are a picture or a source the browser
// cannot handle (3 decode, 4 not supported, which is also what the first request
// being answered with a JSON refusal looks like) and 1, a fetch the browser stopped.
const MEDIA_ERR_NETWORK = 2;

export default {
  name: "AttachmentVideoPlayer",
  props: {
    // One item from normalizeMessageAttachments: { name, file_uid, kind, scan_status, byte_size? }.
    attachment: { type: Object, default: null },
    // attachmentPresentation's action for it: only `play` offers the click.
    action: { type: String, default: "play" },
  },
  data() {
    return {
      // idle | playing | checking | unplayable | scan_pending | unavailable | retry
      phase: "idle",
      // Why a retry is offered: rate_limited | network | error.
      failure: "error",
      // The unplayable card's Try again was used: the next unplayable offers
      // Download only. A release (another file, another player) clears it.
      retriedUnplayable: false,
      downloading: false,
      downloadMessage: "",
      // Bumps whenever the player is released or the item changes, so a probe
      // or a download that answers late is dropped instead of drawn.
      token: 0,
    };
  },
  computed: {
    uid() {
      return this.attachment && isFileUid(this.attachment.file_uid) ? this.attachment.file_uid : null;
    },
    // The same url for every Range request the element makes: no download=1
    // (that is uncapped and not for playback) and never a blob: url.
    videoUrl() {
      return keyonFileUrl(this.uid);
    },
    displayName() {
      return getAttachmentName(this.attachment);
    },
    sizeLabel() {
      return formatAttachmentSize(this.attachment && this.attachment.byte_size);
    },
    // keyon does not store the codec, so the only thing known before the click
    // is the extension: an iPhone's .mov is usually HEVC, which desktop Chrome
    // and Firefox cannot decode.
    advisory() {
      return /\.mov$/i.test(this.displayName) ? "If it will not play here, you can download it." : "";
    },
    canPlay() {
      return this.action === "play" && !!this.videoUrl;
    },
    // A new file or a new scan verdict starts over; a parent re-render that
    // hands down an equal item as a new object does not.
    playbackKey() {
      return `${this.uid}|${(this.attachment && this.attachment.scan_status) || ""}`;
    },
    failed() {
      return withPlaybackFailure(attachmentPresentation(this.attachment, "bubble"));
    },
    unplayableText() {
      return this.failed.stateText || "";
    },
    // Only an item keyon would still serve is offered a download.
    canDownload() {
      return this.failed.action === "download";
    },
    verdictText() {
      return this.phase === "scan_pending" ? SCAN_PENDING_TEXT : DOCUMENT_UNAVAILABLE_TEXT;
    },
    retryText() {
      return RETRY_TEXT[this.failure] || RETRY_TEXT.error;
    },
  },
  watch: {
    playbackKey() {
      this.release();
    },
    action(next) {
      if (next !== "play") this.release();
    },
  },
  beforeUnmount() {
    this.token += 1;
    this.teardownVideo();
    this.freeSlot();
  },
  methods: {
    async startPlayback() {
      if (!this.canPlay || this.phase === "playing") return;
      this.claimSlot();
      this.downloadMessage = "";
      this.phase = "playing";
      await this.$nextTick();
      const el = this.$refs.video;
      if (!el || this.phase !== "playing") return;
      // Autoplay can be refused (iOS wants the call inside the tap); the
      // controls are there either way, so a refusal needs nothing.
      try {
        const started = el.play();
        if (started && typeof started.catch === "function") started.catch(() => null);
      } catch (_) {
        /* the controls remain */
      }
    },
    retry() {
      this.startPlayback();
    },
    retryUnplayable() {
      this.retriedUnplayable = true;
      this.startPlayback();
    },
    // The live element's event, or null: a released element can still fire.
    liveElement(event) {
      const el = this.$refs.video;
      if (this.phase !== "playing" || !el || (event && event.target !== el)) return null;
      return el;
    },
    // Lets go of the element and its stream, and of anything still answering for it.
    dropElement() {
      const token = (this.token += 1);
      this.teardownVideo();
      this.freeSlot();
      return token;
    },
    // keyon takes no file without a video track (VideoContainer, no_video_track),
    // so a picture of 0x0 once the metadata is in is one this browser cannot
    // decode: an iPhone's HEVC in a Chrome or Firefox without HEVC plays the AAC
    // audio under a black box and never fires `error`. keyon served the metadata,
    // so it would serve the file: no probe, straight to the card with Download.
    onMetadata(event) {
      const el = this.liveElement(event);
      if (!el || el.videoWidth !== 0 || el.videoHeight !== 0) return;
      this.dropElement();
      this.phase = "unplayable";
    },
    // A network error (a slice that failed part-way) is worth another try as it
    // is. For anything else the element cannot say why: ask keyon the same
    // question. An answer that is ok means keyon would serve it, so the browser
    // cannot decode it; anything else is a state a card can show.
    async onMediaError(event) {
      const el = this.liveElement(event);
      if (!el) return;
      const code = el.error && el.error.code;
      const token = this.dropElement();
      if (code === MEDIA_ERR_NETWORK) {
        this.failure = "network";
        this.phase = "retry";
        return;
      }
      this.phase = "checking";
      const result = (await probeKeyonVideo(this.uid)) || { ok: false, state: "error" };
      if (token !== this.token) return;
      if (result.ok) {
        this.phase = "unplayable";
      } else if (result.state === "scan_pending" || result.state === "unavailable") {
        this.phase = result.state;
      } else {
        // mfa_required has already sent the person to the code page; if it
        // could not (they are on it), a retry is the honest offer.
        this.failure = result.state === "rate_limited" ? "rate_limited" : "error";
        this.phase = "retry";
      }
    },
    // A plain navigation to file.php?download=1 (never the axios blob path).
    async download() {
      if (this.downloading || !this.uid) return;
      const token = this.token;
      this.downloading = true;
      this.downloadMessage = "";
      let result = null;
      try {
        result = await startKeyonFileDownload(this.uid, this.displayName);
      } finally {
        this.downloading = false;
      }
      if (token !== this.token || !result || result.ok) return;
      if (result.state === "scan_pending" || result.state === "unavailable") {
        this.phase = result.state;
      } else {
        // mfa_required has no sentence: the download already sent the person to the code page.
        this.downloadMessage = downloadRefusalText(result.state) || "";
      }
    },
    claimSlot() {
      if (active && active.owner !== this) active.release();
      active = { owner: this, release: () => this.release() };
    },
    freeSlot() {
      if (active && active.owner === this) active = null;
    },
    // Back to the Play card with nothing open.
    release() {
      this.dropElement();
      this.phase = "idle";
      this.downloadMessage = "";
      this.retriedUnplayable = false;
    },
    // Pausing alone leaves the connection open and the buffer held: dropping
    // the src and load()ing is what makes the browser abandon the stream.
    teardownVideo() {
      const el = this.$refs.video;
      if (!el) return;
      try {
        el.pause();
        el.removeAttribute("src");
        el.load();
      } catch (_) {
        /* nothing left to release */
      }
    },
  },
};
</script>

<style scoped lang="scss">
.attachment-video {
  width: 100%;
}

// A fixed 16:9 box whatever the video, so the thread's height is the same
// before the click, while it plays and when the metadata arrives (the widgets
// scroll to the bottom on load); max-height keeps a tall phone video from
// filling a small window.
.video-box {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 60vh;
  border-radius: 8px;
  overflow: hidden;
  background: #111;
  color: #fff;

  video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
}

.video-idle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px;
  box-sizing: border-box;
}

.video-play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 18px;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid #8ab4ff;
    outline-offset: 2px;
  }
}

.video-idle-icon {
  font-size: 28px;
  opacity: 0.8;
}

.video-caption {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2px 8px;
  max-width: 100%;
  font-size: 12px;
  text-align: center;
}

.video-name {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-size,
.video-advisory {
  opacity: 0.8;
}

.video-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: inherit;
}

.video-card-text {
  flex: 1 1 100%;
}

.video-state {
  cursor: default;
  opacity: 0.75;
}

.video-card-name {
  flex: 1 1 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: 0.8;
}

.video-action {
  padding: 4px 12px;
  border: 1px solid currentColor;
  border-radius: 14px;
  background: transparent;
  color: inherit;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}
</style>
